'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import Classes from './style.module.scss';

import { EnvironmentService } from '@/lib/environmentVariables';
import { cn } from '@/lib/utils';

import { shareStore } from '@/store/share';

import { Share } from '@/models/SocialMedia';
import { ShareActive } from '@/models/States';

import IconButton from '../buttons/IconButton';
import { CloseIcon, FacebookIcon, LinkedinIcon, TwitterIcon } from '../icons';
import UnstyledLink from '../links/UnstyledLink';

export default function ShareArticleModal() {
  const [show, setShow] = useState<boolean>(false);
  const [share, setShare] = useState<Share>();
  const [pageDescription, setPageDescription] = useState<string>();
  const pathname = usePathname();
  const pageURL = EnvironmentService.getCanonicalURL().replace(':', '%3A');
  const activeNav = shareStore((store: ShareActive) => store.navigation);
  const toggleNav = shareStore(
    (store: ShareActive) => store.toggleShareNavigation,
  );

  useEffect(() => {
    setShow(activeNav.opened);

    activeNav?.share && setShare(activeNav.share);

    activeNav?.pageDescription && setPageDescription(activeNav.pageDescription);
  }, [activeNav]);

  useEffect(() => {
    if (!show) {
      setShow(false);
      toggleNav(false, undefined);
    }
  }, [show, toggleNav]);

  return (
    <div
      className={cn(
        'share-modal fixed bottom-0 left-1/2 z-[9999] flex w-screen -translate-x-1/2 transform flex-col gap-3 bg-white px-6 py-4 text-primary drop-shadow-[0_0px_10px_rgba(0,0,0,0.2)] sm:px-0',
        show
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-full opacity-0',
      )}
    >
      <div className='container'>
        <div className='flex items-center justify-between'>
          <p className='text-lg font-semibold'>Share to...</p>
          <IconButton
            icon={CloseIcon}
            variant='ghost'
            className='hover:bg-transparent'
            onClick={() => setShow(false)}
            aria-label='Close share modal'
          />
        </div>
        {share && (
          <ul className='flex flex-col gap-6 pt-5'>
            {share?.facebook && (
              <li className={Classes.shareButtonArticleItem}>
                <UnstyledLink
                  href={`https://www.facebook.com/sharer/sharer.php?u=${pageURL}${pathname}`}
                  className={Classes.shareButtonArticleLink}
                  aria-label='Share on Facebook'
                  openNewTab
                >
                  <FacebookIcon
                    fill='#010F1D'
                    className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                  />
                  <span className='pointer-events-none'>Facebook</span>
                </UnstyledLink>
              </li>
            )}
            {share?.linkedin && (
              <li className={Classes.shareButtonArticleItem}>
                <UnstyledLink
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${pageURL}${pathname}`}
                  className={Classes.shareButtonArticleLink}
                  aria-label='Share on Linkedin'
                  openNewTab
                >
                  <LinkedinIcon
                    fill='#010F1D'
                    className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                  />
                  <span className='pointer-events-none'>Linkedin</span>
                </UnstyledLink>
              </li>
            )}
            {share?.twitter && pageDescription && (
              <li className={Classes.shareButtonArticleItem}>
                <UnstyledLink
                  href={`https://twitter.com/intent/tweet?text=${pageDescription}%0ARead%20more%3A%20${pageURL}${pathname}`}
                  className={Classes.shareButtonArticleLink}
                  aria-label='Share on Twitter'
                  openNewTab
                >
                  <TwitterIcon
                    fill='#010F1D'
                    className='pointer-events-none h-6 w-6 fill-primary sm:h-[26px] sm:w-[26px]'
                  />
                  <span className='pointer-events-none'>Twitter</span>
                </UnstyledLink>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
